// dependecies
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { faGear } from "@fortawesome/pro-solid-svg-icons";

// components
import { DropDownMenu } from "@performyard/py-components";

import Icon from "../../../../../../../components/Icon";
import Typography from "../../../../../../../components/Text";

// hooks
import {
	useGoalDetails,
	useGoalHistory,
	useMutateGoalDetails,
	useRbacObjectPermissions,
} from "../../../../../../../api/hooks";
import useSearchQueryParams from "../../../../../../../hooks/useSearchQueryParams";

import { Goal } from "../../../../../../../api/services";

import { parseGoalPermissionsForUser } from "../../../../utils";
// utils
import { getGoalDropdownOptions } from "../../utils";

//config
import Config from "../../../../config";
//css
import css from "../../SingleGoalHeader.module.scss";

const GoalDetailsOptionsDropdown = () => {
	const navigate = useNavigate();
	const { goal_id: goalId } = useParams<{ goal_id: string }>();

	const { handleQueryChange } = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const { refetch: refetchProgressHistory } = useGoalHistory({
		goalID: goalId || "",
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { refetch: refetchActivityHistory } = useGoalHistory({
		goalID: goalId || "",
		sortDirection: "desc",
		sortName: "_id",
	});

	const refetchHistory = () => {
		refetchProgressHistory();
		refetchActivityHistory();
	};

	const { data, refetch: refetchGoal } = useGoalDetails({ goalID: goalId || "" });
	const goal = data ? data.goal : ({} as Goal);
	const { data: permissions } = useRbacObjectPermissions("goal", goalId || "");

	const dropdownOptions = useMemo(() => {
		const goalPermissions = parseGoalPermissionsForUser(permissions);

		return getGoalDropdownOptions(goal, goalPermissions);
	}, [permissions, JSON.stringify(goal)]);

	const { mutate } = useMutateGoalDetails(goalId || "", {
		onSuccess: () => {
			handleQueryChange({ modal: undefined });
			refetchGoal();
			refetchHistory();
		},
	});

	const handleOptionsSelect = (key: string) => {
		switch (key) {
			case "edit":
				navigate(`/goal/${goal?._id}/edit/`);
				break;

			case "duplicate":
				navigate(`/goal/${goal?._id}/copy/`);
				break;

			case "archive":
				handleQueryChange({ modal: "archive" });
				break;

			case "reopen":
				mutate([{ field: "status", value: "active" }]);
				break;

			case "complete":
				handleQueryChange({ modal: "complete" });
				break;

			case "delete":
				handleQueryChange({ modal: "delete" });
				break;

			case "update":
				handleQueryChange({ modal: "updateProgress" });
				break;

			case "share":
				handleQueryChange({ modal: "share" });
				break;
		}
	};

	if (dropdownOptions.length === 0) {
		return null;
	}

	return (
		<div>
			<DropDownMenu
				className={`w-[160px] ${css.optionsMenu}`}
				onSelect={handleOptionsSelect}
				options={dropdownOptions}
				testid="goal-details-options-dropdown"
				placement="bottom"
				bordered
			>
				<Typography
					size="paragraph-3"
					weight="regular"
					color="neutral-9"
					className="flex w-full items-center justify-between gap-[4px]"
				>
					<div>Options</div>
					<Icon icon={faGear} size="inherit" />
				</Typography>
			</DropDownMenu>
		</div>
	);
};

export default GoalDetailsOptionsDropdown;
